import { useQuery } from '@tanstack/react-query';
import { harborApi } from '@/utils/api';

interface HarborLabel {
  id: string;
  name: string;
}

interface HarborLabelWeight {
  labelId: string;
  weight: number;
}

interface HarborDomainWeight {
  domain: string;
  weight: number;
}

export function useHarborAccounts() {
  return useQuery({
    queryKey: ['harbor', 'accounts'],
    queryFn: () => harborApi.getAccounts(),
  });
}

export function useHarborThreads(accountEmails: string[]) {
  return useQuery({
    queryKey: ['harbor', 'threads', accountEmails],
    queryFn: () => harborApi.getThreads(accountEmails),
  });
}

export function useHarborSummary(accountEmails: string[]) {
  return useQuery({
    queryKey: ['harbor', 'summary', accountEmails],
    queryFn: () => harborApi.getSummary(accountEmails),
  });
}

export function useHarborCategories() {
  return useQuery({
    queryKey: ['harbor', 'categories'],
    queryFn: () => harborApi.getCategories(),
  });
}

export function useHarborMetadata() {
  return useQuery({
    queryKey: ['harbor', 'metadata'],
    queryFn: async () => {
      const [labels, lWeights, dWeights, mappings] = await Promise.all([
        harborApi.getLabels().catch(() => []),
        harborApi.getLabelWeights().catch(() => []),
        harborApi.getDomainWeights().catch(() => []),
        harborApi.getLabelMappings().catch(() => []),
      ]);

      const labelMap: Record<string, string> = {};
      (labels as HarborLabel[]).forEach((l) => {
        labelMap[l.id] = l.name;
      });

      const labelWeights: Record<string, number> = {};
      (lWeights as HarborLabelWeight[]).forEach((w) => {
        labelWeights[w.labelId] = w.weight;
      });

      const domainWeights: Record<string, number> = {};
      (dWeights as HarborDomainWeight[]).forEach((w) => {
        domainWeights[w.domain] = w.weight;
      });

      return { labelMap, labelWeights, domainWeights, mappings };
    },
  });
}

export function useWorkspaceMembers() {
  return useQuery({
    queryKey: ['workspace', 'members'],
    queryFn: () => harborApi.getWorkspaceMembers(),
  });
}
